/*
 * Fiche d'un participant d'un groupe, dans le style du CRM.
 * Elle lit et enregistre directement la fiche participant (table participants),
 * avec les mêmes champs obligatoires que le formulaire participant de la page admin.
 */
import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import type { Directory, ParticipantRow, Role, SharingOperation } from './types';
import { PERIOD_LABELS, type ReimbursementPeriod, ROLE_LABELS, formatBce } from './types';
import { Field, SuggestInput, borderFor, inputClass, errorMessage } from './ui';
import { VAT_RATE_OPTIONS, fromMwh, toMwh, updateParticipant } from './participants';

interface Props {
  participant: ParticipantRow;
  groupName: string;
  operations: SharingOperation[];
  directory: Directory;
  onBack: () => void;
  onSaved: (p: ParticipantRow) => void;
}

interface Form {
  name: string;
  type: Role;
  company_name: string;
  company_number: string;
  send_email: string;
  address: string;
  sharing_operation_id: string;
  entry_date: string;
  price: string;
  sell_price: string;
  adhesion_fee: string;
  vat_rate: string;
  reimbursement_period: ReimbursementPeriod;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const num = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));

function toForm(p: ParticipantRow): Form {
  const type: Role = p.type ?? 'consumer';
  return {
    name: p.name ?? '',
    type,
    company_name: p.company_name ?? '',
    company_number: p.company_number ?? '',
    send_email: p.send_email ?? '',
    address: p.address ?? '',
    sharing_operation_id: p.sharing_operation_id ?? '',
    entry_date: (p.entry_date ?? '').slice(0, 10),
    price: num(fromMwh(type === 'prosumer' ? p.prosumer_buy_price ?? p.shared_energy_price : p.shared_energy_price)),
    sell_price: num(fromMwh(p.prosumer_sell_price)),
    adhesion_fee: num(p.adhesion_fee),
    vat_rate: num(p.vat_rate),
    reimbursement_period: p.reimbursement_period ?? 'quarterly',
  };
}

/** Colonnes de la table participants calculées depuis le formulaire (mêmes conversions que le formulaire admin). */
function columnsFor(form: Form, variable: boolean): Record<string, unknown> {
  const prosumer = form.type === 'prosumer';
  const cols: Record<string, unknown> = {
    name: form.name.trim(),
    type: form.type,
    company_name: form.company_name.trim() || null,
    company_number: formatBce(form.company_number),
    send_email: form.send_email.trim().toLowerCase() || null,
    address: form.address.trim(),
    sharing_operation_id: form.sharing_operation_id || null,
    entry_date: form.entry_date || null,
    // Comme le formulaire admin : 50 €/an si la cotisation est vide.
    adhesion_fee: form.adhesion_fee.trim() ? Number(form.adhesion_fee) : 50,
    vat_rate: form.vat_rate === '' ? null : Number(form.vat_rate),
    // Consommateur : toujours facturé chaque mois ; producteur et prosumer : période de remboursement.
    reimbursement_period: form.type === 'consumer' ? null : form.reimbursement_period,
  };
  if (!variable) {
    const buy = form.price.trim() ? toMwh(Number(form.price)) : null;
    cols.shared_energy_price = buy;
    cols.prosumer_buy_price = prosumer ? buy : null;
    cols.prosumer_sell_price = prosumer && form.sell_price.trim() ? toMwh(Number(form.sell_price)) : null;
  }
  return cols;
}

export function ParticipantPanel({ participant, groupName, operations, directory, onBack, onSaved }: Props) {
  const [row, setRow] = useState(participant);
  const [form, setForm] = useState<Form>(() => toForm(participant));
  const [saving, setSaving] = useState(false);
  const variable = row.shared_energy_formula_type === 'variable';
  const isProsumer = form.type === 'prosumer';
  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm(prev => ({ ...prev, [key]: value }));

  const knownName = directory.companyNumberToName[formatBce(form.company_number) ?? ''];
  const errors = useMemo(() => {
    const e: Partial<Record<keyof Form, string>> = {};
    if (!form.name.trim()) e.name = 'Le nom du participant est requis';
    if (!form.address.trim()) e.address = 'L’adresse est requise';
    if (!form.entry_date) e.entry_date = 'La date d’entrée est requise';
    if (!form.sharing_operation_id) e.sharing_operation_id = 'L’opération est requise';
    if (form.send_email.trim() && !EMAIL_RE.test(form.send_email.trim())) e.send_email = 'Format d’e-mail invalide';
    if (form.company_number.trim() && form.company_number.replace(/\D/g, '').length !== 10) e.company_number = 'Format invalide. Utilisez : BE 0123.456.789';
    if (knownName && form.company_name.trim() && form.company_name.trim().toLowerCase() !== knownName.toLowerCase()) {
      e.company_name = `Ce numéro d’entreprise correspond déjà à « ${knownName} ». Utilisez le même nom pour éviter une erreur de regroupement dans les factures.`;
    }
    const priceError = (v: string, label: string) => {
      if (!v.trim()) return `${label} est requis`;
      const n = Number(v);
      return n > 0 && n <= 100 ? undefined : 'Prix attendu entre 0 et 100 c€/kWh';
    };
    if (!variable) {
      e.price = priceError(form.price, isProsumer ? 'Le prix d’achat' : 'Le prix de l’énergie partagée');
      if (isProsumer) e.sell_price = priceError(form.sell_price, 'Le prix de vente');
    }
    if (form.adhesion_fee.trim() && !(Number(form.adhesion_fee) >= 0)) e.adhesion_fee = 'Cotisation invalide';
    return Object.fromEntries(Object.entries(e).filter(([, v]) => v)) as Partial<Record<keyof Form, string>>;
  }, [form, variable, isProsumer, knownName]);

  /** Colonnes modifiées : comparaison avec la fiche telle qu'ouverte (un champ non touché n'est jamais réécrit). */
  const baseline = useMemo(() => columnsFor(toForm(row), variable), [row, variable]);
  const patch = useMemo(() => {
    const next = columnsFor(form, variable);
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(next)) if (JSON.stringify(v ?? null) !== JSON.stringify(baseline[k] ?? null)) out[k] = v;
    return out;
  }, [form, baseline, variable]);
  const dirty = Object.keys(patch).length > 0;

  const save = async () => {
    if (Object.keys(errors).length > 0) {
      toast.error(Object.values(errors)[0] ?? 'Corrigez les champs en rouge');
      return;
    }
    if (!dirty) return;
    setSaving(true);
    try {
      const updated = await updateParticipant(row.id, patch);
      setRow(updated);
      setForm(toForm(updated));
      onSaved(updated);
      toast.success(`Participant « ${updated.name} » mis à jour`);
    } catch (err) {
      toast.error(`Erreur : ${errorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const back = () => {
    if (dirty && !window.confirm('Les modifications de ce participant ne sont pas enregistrées. Revenir au groupe ?')) return;
    onBack();
  };

  const text = (key: keyof Form, label: string, opts: { required?: boolean; type?: string; placeholder?: string; full?: boolean } = {}) => (
    <Field label={label} required={opts.required} error={errors[key]} className={opts.full ? 'col-span-2' : ''}>
      <input
        type={opts.type ?? 'text'}
        step={opts.type === 'number' ? 'any' : undefined}
        value={form[key]}
        placeholder={opts.placeholder}
        onChange={e => set(key, e.target.value as Form[typeof key])}
        className={`${inputClass} ${borderFor(false, errors[key])}`}
      />
    </Field>
  );
  const sectionTitle = (t: string) => <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{t}</h4>;
  const vatOptions = VAT_RATE_OPTIONS.some(o => String(o.value) === form.vat_rate) || form.vat_rate === ''
    ? VAT_RATE_OPTIONS
    : [...VAT_RATE_OPTIONS, { value: Number(form.vat_rate), label: `${Math.round(Number(form.vat_rate) * 1000) / 10} %` }];

  return (
    <div className="absolute inset-0 z-10 bg-white flex flex-col">
      <div className="px-6 py-4 border-b border-neutral-100 shrink-0">
        <button onClick={back} className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-800 mb-2">
          <ArrowLeft className="w-3.5 h-3.5" />Retour au groupe « {groupName} »
        </button>
        <h3 className="text-base font-semibold text-neutral-900 truncate">{form.name.trim() || 'Participant sans nom'}</h3>
        <p className="text-xs text-neutral-500 mt-0.5">
          Participant du groupe « {groupName} » · <span className="font-mono text-[11px]">{row.ean_code}</span>
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        <section className="space-y-3">
          {sectionTitle('Identité')}
          <div className="grid grid-cols-2 gap-3">
            {text('name', 'Nom du participant', { required: true, full: true })}
            <div className="col-span-2 grid grid-cols-3 gap-2">
              {(Object.keys(ROLE_LABELS) as Role[]).map(r => (
                <button key={r} type="button" onClick={() => set('type', r)}
                  className={`py-2 rounded-lg text-xs font-semibold border ${form.type === r ? 'border-[#F8BD3D] bg-amber-50 text-neutral-900' : 'border-neutral-200 text-neutral-500 hover:border-neutral-300'}`}>
                  {ROLE_LABELS[r]}
                </button>
              ))}
            </div>
            <Field label="Dénomination" error={errors.company_name} className="col-span-2">
              <SuggestInput
                value={form.company_name}
                onChange={v => set('company_name', v)}
                suggestions={directory.companyNames.map(n => ({ value: n }))}
                placeholder="Dénomination de la société"
                className={errors.company_name ? 'border-red-300' : 'border-neutral-200'}
              />
            </Field>
            {text('company_number', 'N° d’entreprise', { placeholder: 'BE 0123.456.789' })}
            <Field label="Groupe">
              <input value={groupName} disabled className={`${inputClass} border-neutral-200`} />
            </Field>
          </div>
        </section>

        <section className="space-y-3">
          {sectionTitle('Coordonnées')}
          <div className="grid grid-cols-2 gap-3">
            {text('send_email', 'E-mail de facturation', { type: 'email', placeholder: 'facturation@societe.be', full: true })}
          </div>
        </section>

        <section className="space-y-3">
          {sectionTitle('Compteur')}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code EAN">
              <input value={row.ean_code ?? ''} disabled className={`${inputClass} font-mono text-[13px] border-neutral-200`} />
            </Field>
            {text('address', 'Adresse', { required: true })}
          </div>
        </section>

        <section className="space-y-3">
          {sectionTitle('Partage')}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Opération de partage" required error={errors.sharing_operation_id} className="col-span-2">
              <select value={form.sharing_operation_id} onChange={e => set('sharing_operation_id', e.target.value)} className={`${inputClass} ${borderFor(false, errors.sharing_operation_id)}`}>
                <option value="">Sélectionner…</option>
                {operations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </Field>
            {text('entry_date', 'Date d’entrée', { required: true, type: 'date' })}
            {variable ? (
              <p className="text-[11px] text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg p-2.5 self-end">
                Prix indexé (formule A × X) : il se modifie dans le formulaire participant de la page admin.
              </p>
            ) : text('price', isProsumer ? 'Prix d’achat (c€/kWh HTVA)' : 'Prix (c€/kWh HTVA)', { required: true, type: 'number' })}
            {isProsumer && !variable && text('sell_price', 'Prix de vente (c€/kWh HTVA)', { required: true, type: 'number' })}
            {text('adhesion_fee', 'Cotisation annuelle (€/an HTVA)', { type: 'number', placeholder: '50' })}
            <Field label="TVA appliquée">
              <select value={form.vat_rate} onChange={e => set('vat_rate', e.target.value)} className={`${inputClass} border-neutral-200`}>
                <option value="">Non renseignée</option>
                {vatOptions.map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
              </select>
            </Field>
            {form.type !== 'consumer' && (
              <Field label="Période de remboursement">
                <select value={form.reimbursement_period} onChange={e => set('reimbursement_period', e.target.value as ReimbursementPeriod)} className={`${inputClass} border-neutral-200`}>
                  {(Object.keys(PERIOD_LABELS) as ReimbursementPeriod[]).map(p => (
                    <option key={p} value={p}>{PERIOD_LABELS[p]}{p === 'quarterly' ? ' (par défaut)' : ''}</option>
                  ))}
                </select>
              </Field>
            )}
          </div>
          <p className="text-[11px] text-neutral-500">
            Enregistré dans la fiche participant, comme avec le formulaire de la page admin. Tarifs fournisseur et formules : formulaire de la page admin.
          </p>
        </section>
      </div>

      <div className="px-6 py-4 border-t border-neutral-100 bg-neutral-50/60 flex items-center justify-between gap-3 shrink-0">
        <button onClick={back} className="px-3 py-2 rounded-lg text-xs text-neutral-600 hover:bg-neutral-100">Retour au groupe</button>
        <button onClick={save} disabled={saving || !dirty} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-sm font-semibold text-neutral-900 disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer
        </button>
      </div>
    </div>
  );
}
